import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { delay, finalize, map, switchMap } from 'rxjs/operators';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { CheckinTemplateDto } from '../../models/checkin-template/checkin-template-dto.model';
import { CheckinTemplateDetailMenuEnum } from '../../enums/checkin-template/checkin-template-detail-menu.enum';
import { ClassTreeNode } from '../../models/frontend_entities/ClassTreeNode';
import { CheckinTemplateDetailAction } from './checkin-template-detail.actions';
import { CheckinTemplateRequestService } from '../../services/request/checkin-template-request.service';
import { CompoApp } from '../../services/CompoApp';
import { calcDefaultTimeoutOf } from '../../services/client/request-client.service';
import {
  buildCheckinTemplateClassTree,
  cloneAndMarkCheckinTemplateNode,
  getCheckinTemplateAllLeafClassIds
} from '../../utils/checkin-template-target-class-tree.util';
import { ResetStateDefaults } from '../base-action-class.model';
import { CheckinTemplateReferenceDto } from '../../models/checkin-template/checkin-template-reference-dto.model';
import { CheckinTemplateReferenceInputDto } from '../../models/checkin-template/checkin-template-reference-input-dto.model';
import { moveItemInArray } from '@angular/cdk/drag-drop';

export interface CheckinTemplateDetailStateModel {
  id: string | null;
  checkinTemplate: CheckinTemplateDto | null;
  selectedMenu: CheckinTemplateDetailMenuEnum;
  inProgress: boolean;
  initialized: boolean;

  classTree: ClassTreeNode[] | null;
  allClassIds: string[];
  allLeafClassIds: string[];
  initialSelection: string[];
  currentSelection: string[];
  currentSelectionOnlyLeafs: string[];
  disabledClassIds: string[];
  classesLoaded: boolean;
  classesInProgress: boolean;

  classNames: Record<string, string>;
  initialReferences: CheckinTemplateReferenceDto[];
  references: CheckinTemplateReferenceDto[];
  referencesLoaded: boolean;
  referencesInProgress: boolean;
}

const getDefaults = (): CheckinTemplateDetailStateModel => ({
  id: null,
  checkinTemplate: null,
  selectedMenu: CheckinTemplateDetailMenuEnum.CLASSES,
  inProgress: false,
  initialized: false,

  classTree: null,
  allClassIds: [],
  allLeafClassIds: [],
  initialSelection: [],
  currentSelection: [],
  currentSelectionOnlyLeafs: [],
  disabledClassIds: [],
  classesLoaded: false,
  classesInProgress: false,

  classNames: {},
  initialReferences: [],
  references: [],
  referencesLoaded: false,
  referencesInProgress: false
});

@State<CheckinTemplateDetailStateModel>({
  name: 'checkinTemplateDetail',
  defaults: getDefaults()
})
@Injectable()
export class CheckinTemplateDetailXsState {

  //region INJECTIONS
  private readonly app: CompoApp = inject(CompoApp);
  private readonly checkinTemplateRequestService: CheckinTemplateRequestService = inject(CheckinTemplateRequestService);
  //endregion

  //region SELECTORS
  @Selector()
  public static id(state: CheckinTemplateDetailStateModel): string | null {
    return state.id;
  }

  @Selector()
  public static checkinTemplate(state: CheckinTemplateDetailStateModel): CheckinTemplateDto | null {
    return state.checkinTemplate;
  }

  @Selector()
  public static selectedMenu(state: CheckinTemplateDetailStateModel): CheckinTemplateDetailMenuEnum {
    return state.selectedMenu;
  }

  @Selector()
  public static inProgress(state: CheckinTemplateDetailStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  public static initialized(state: CheckinTemplateDetailStateModel): boolean {
    return state.initialized;
  }

  @Selector()
  public static targetClassCount(state: CheckinTemplateDetailStateModel): number {
    return state.checkinTemplate?.targetClassCount ?? 0;
  }

  @Selector()
  public static referenceCount(state: CheckinTemplateDetailStateModel): number {
    return state.referencesLoaded
      ? state.references.length
      : (state.checkinTemplate?.referenceCount ?? 0);
  }

  @Selector()
  public static references(state: CheckinTemplateDetailStateModel): CheckinTemplateReferenceDto[] {
    return state.references;
  }

  @Selector()
  public static referencesLoaded(state: CheckinTemplateDetailStateModel): boolean {
    return state.referencesLoaded;
  }

  @Selector()
  public static referencesInProgress(state: CheckinTemplateDetailStateModel): boolean {
    return state.referencesInProgress;
  }

  @Selector()
  public static classNames(state: CheckinTemplateDetailStateModel): Record<string, string> {
    return state.classNames;
  }

  @Selector()
  public static referencesSaveRequired(state: CheckinTemplateDetailStateModel): boolean {
    return !CheckinTemplateDetailXsState.areReferencesEqual(state.references, state.initialReferences);
  }

  @Selector()
  public static assignedReferenceClassIds(state: CheckinTemplateDetailStateModel): Set<string> {
    const classIds = new Set<string>();
    for (const ref of state.references || []) {
      for (const cid of ref.classIds || []) {
        classIds.add(cid);
      }
    }
    return classIds;
  }

  @Selector()
  public static classTree(state: CheckinTemplateDetailStateModel): ClassTreeNode[] | null {
    return state.classTree;
  }

  @Selector()
  public static allClassIds(state: CheckinTemplateDetailStateModel): string[] {
    return state.allClassIds;
  }

  @Selector()
  public static allLeafClassIds(state: CheckinTemplateDetailStateModel): string[] {
    return state.allLeafClassIds;
  }

  @Selector()
  public static classSelection(state: CheckinTemplateDetailStateModel): Set<string> {
    return new Set<string>(state.currentSelection);
  }

  @Selector()
  public static classSelectionOnlyLeafs(state: CheckinTemplateDetailStateModel): Set<string> {
    return new Set<string>(state.currentSelectionOnlyLeafs);
  }

  @Selector()
  public static targetClassIds(state: CheckinTemplateDetailStateModel): Set<string> {
    return new Set<string>(state.initialSelection || []);
  }

  @Selector()
  public static disabledClassIds(state: CheckinTemplateDetailStateModel): Set<string> {
    const set = new Set<string>(state.disabledClassIds || []);
    for (const ref of state.references || []) {
      for (const cid of ref.classIds || []) {
        set.add(cid);
      }
    }
    return set;
  }

  @Selector()
  public static classesLoaded(state: CheckinTemplateDetailStateModel): boolean {
    return state.classesLoaded;
  }

  @Selector()
  public static classesInProgress(state: CheckinTemplateDetailStateModel): boolean {
    return state.classesInProgress;
  }

  @Selector()
  public static classesSaveRequired(state: CheckinTemplateDetailStateModel): boolean {
    return !CheckinTemplateDetailXsState.areArraysEqualAsSets(state.currentSelection, state.initialSelection);
  }

  private static areArraysEqualAsSets(arr1: string[] | null | undefined, arr2: string[] | null | undefined): boolean {
    if (!arr1 || !arr2) return arr1 === arr2;
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);
    return set1.size === set2.size && [...set1].every(x => set2.has(x));
  }

  private static areReferencesEqual(
    arr1: CheckinTemplateReferenceDto[] | null | undefined,
    arr2: CheckinTemplateReferenceDto[] | null | undefined
  ): boolean {
    if (!arr1 || !arr2) return arr1 === arr2;
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      const a = arr1[i];
      const b = arr2[i];
      if ((a.name || '') !== (b.name || '')) return false;
      if (a.required !== b.required) return false;
      const minA = a.minQuantity ?? null;
      const minB = b.minQuantity ?? null;
      if (minA !== minB) return false;
      if (!CheckinTemplateDetailXsState.areArraysEqualAsSets(a.classIds, b.classIds)) return false;
    }
    return true;
  }
  //endregion

  //region ACTIONS
  @Action(CheckinTemplateDetailAction.LoadCheckinTemplate)
  public loadCheckinTemplate(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.LoadCheckinTemplate
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checkin template request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('Checkin template id must be set!'));
    }
    if (state.id === action.id && state.checkinTemplate) {
      return of(undefined);
    }

    const isDifferentTemplate = state.id !== action.id;
    if (isDifferentTemplate) {
      ctx.setState({
        ...getDefaults(),
        selectedMenu: state.selectedMenu,
        id: action.id,
        inProgress: true
      });
    } else {
      ctx.patchState({
        id: action.id,
        checkinTemplate: null,
        inProgress: true
      });
    }

    const startDate: Date = new Date();

    return this.checkinTemplateRequestService.getById(action.id).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      map((template: CheckinTemplateDto) => {
        const currentState = ctx.getState();
        if (currentState.id !== action.id) {
          return;
        }
        const resolvedTemplate: CheckinTemplateDto = {
          ...template,
          referenceCount: currentState.referencesLoaded
            ? currentState.references.length
            : template.referenceCount
        };
        ctx.patchState({
          id: resolvedTemplate.id,
          checkinTemplate: resolvedTemplate,
          initialized: true
        });
      }),
      finalize(() => {
        ctx.patchState({
          inProgress: false
        });
      })
    );
  }

  @Action(CheckinTemplateDetailAction.SelectMenu)
  public selectMenu(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.SelectMenu
  ): void {
    const state = ctx.getState();

    if (state.inProgress) {
      throw new Error('Checkin template request is in progress!');
    }
    if (!action.menu) {
      throw new Error('Menu must be set!');
    }

    ctx.patchState({
      selectedMenu: action.menu
    });
  }

  @Action(CheckinTemplateDetailAction.DeleteCheckinTemplate)
  public deleteCheckinTemplate(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.DeleteCheckinTemplate
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checkin template request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('Checkin template id must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const startDate: Date = new Date();

    return this.checkinTemplateRequestService.delete(action.id).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      finalize(() => {
        ctx.patchState({
          inProgress: false
        });
      })
    );
  }

  @Action(ResetStateDefaults)
  @Action(CheckinTemplateDetailAction.ResetState)
  public resetState(ctx: StateContext<CheckinTemplateDetailStateModel>): void {
    ctx.setState(getDefaults());
  }

  @Action(CheckinTemplateDetailAction.UpdateInfo)
  public updateInfo(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.UpdateInfo
  ): Observable<void> {
    const state = ctx.getState();
    const templateId = state.id;
    if (!templateId) {
      return throwError(() => new Error('State is not initialized!'));
    }
    if (state.inProgress) {
      return throwError(() => new Error('Checkin template request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const startDate: Date = new Date();

    return this.checkinTemplateRequestService.update(templateId, action.dto).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      map((updated: CheckinTemplateDto) => {
        const currentState = ctx.getState();
        if (currentState.id !== templateId) {
          return;
        }
        ctx.patchState({
          checkinTemplate: {
            ...updated,
            referenceCount: currentState.referencesLoaded
              ? currentState.references.length
              : updated.referenceCount
          }
        });
      }),
      finalize(() => {
        ctx.patchState({
          inProgress: false
        });
      })
    );
  }

  @Action(CheckinTemplateDetailAction.LoadClasses)
  public loadClasses(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.LoadClasses
  ): Observable<void> {
    const state = ctx.getState();
    const templateId = state.id;
    if (!templateId) {
      return throwError(() => new Error('State is not initialized!'));
    }
    if (state.classesInProgress && !action?.force) {
      return of(undefined);
    }
    if (state.classesLoaded && !action?.force) {
      return of(undefined);
    }

    ctx.patchState({
      classesInProgress: true
    });

    const user = this.app.data.getUser();
    const orgId = user?.organization?.id;
    const start: Date = new Date();

    const refs$ = state.referencesLoaded
      ? of(state.references)
      : this.checkinTemplateRequestService.getReferences(templateId);

    return forkJoin({
      compoClasses: from(this.app.classService.getClassTreeForOrg(orgId)),
      assignedClasses: this.checkinTemplateRequestService.getTargetClasses(templateId),
      refs: refs$
    }).pipe(
      delay(calcDefaultTimeoutOf(start)),
      map(({ compoClasses, assignedClasses, refs }) => {
        const currentState = ctx.getState();
        if (currentState.id !== templateId) {
          return;
        }

        const classNames: Record<string, string> = { ...currentState.classNames };
        for (const cl of compoClasses || []) {
          if (cl.id && cl.name) {
            classNames[cl.id] = cl.name;
          }
        }

        const referenceList = currentState.referencesLoaded
          ? currentState.references
          : (refs || []);

        const referenceClassIds = new Set<string>();
        for (const ref of referenceList) {
          for (const cid of ref.classIds || []) {
            referenceClassIds.add(cid);
          }
        }

        const classTree = buildCheckinTemplateClassTree(compoClasses, referenceClassIds);
        const allLeafClassIds = getCheckinTemplateAllLeafClassIds(compoClasses);
        const allClassIds = (compoClasses || []).map(c => c.id).filter(Boolean);

        const initialSelection = assignedClasses || [];

        const updatedTemplate: CheckinTemplateDto | null = currentState.checkinTemplate
          ? {
              ...currentState.checkinTemplate,
              targetClassCount: initialSelection.length,
              referenceCount: referenceList.length
            }
          : null;

        ctx.patchState({
          classTree,
          allClassIds,
          allLeafClassIds,
          initialSelection: [...initialSelection],
          currentSelection: [...initialSelection],
          currentSelectionOnlyLeafs: [...initialSelection],
          disabledClassIds: Array.from(referenceClassIds),
          classesLoaded: true,
          classNames,
          references: referenceList,
          initialReferences: currentState.referencesLoaded ? currentState.initialReferences : referenceList,
          referencesLoaded: true,
          checkinTemplate: updatedTemplate
        });
      }),
      finalize(() => {
        if (ctx.getState().id === templateId) {
          ctx.patchState({
            classesLoaded: true,
            classesInProgress: false
          });
        } else {
          ctx.patchState({
            classesInProgress: false
          });
        }
      })
    );
  }

  @Action(CheckinTemplateDetailAction.ChangeClassSelection)
  public changeClassSelection(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.ChangeClassSelection
  ): void {
    const state = ctx.getState();
    const disabledSet = CheckinTemplateDetailXsState.disabledClassIds(state);
    const leafSet = new Set(state.allLeafClassIds || []);
    const onlyLeafs = Array.from(action.ids).filter(id => leafSet.has(id) && !disabledSet.has(id));
    const sanitizedSelection = Array.from(action.ids).filter(id => !disabledSet.has(id));

    ctx.patchState({
      currentSelection: sanitizedSelection,
      currentSelectionOnlyLeafs: onlyLeafs
    });
  }

  @Action(CheckinTemplateDetailAction.SaveClassSelection)
  public saveClassSelection(
    ctx: StateContext<CheckinTemplateDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();
    const templateId = state.id;
    if (!templateId) {
      return throwError(() => new Error('State is not initialized!'));
    }

    ctx.patchState({
      classesInProgress: true
    });

    const selection = state.currentSelectionOnlyLeafs || [];
    const startDate = new Date();

    return this.checkinTemplateRequestService.saveTargetClasses(templateId, selection).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      map(() => {
        const currentState = ctx.getState();
        if (currentState.id !== templateId) {
          return;
        }

        const updatedTemplate: CheckinTemplateDto | null = currentState.checkinTemplate
          ? { ...currentState.checkinTemplate, targetClassCount: selection.length }
          : null;

        ctx.patchState({
          initialSelection: selection,
          checkinTemplate: updatedTemplate
        });
      }),
      finalize(() => {
        ctx.patchState({
          classesInProgress: false
        });
      })
    );
  }

  @Action(CheckinTemplateDetailAction.ResetClassSelection)
  public resetClassSelection(
    ctx: StateContext<CheckinTemplateDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();
    const initialSelection = [...(state.initialSelection || [])];

    ctx.patchState({
      currentSelection: initialSelection,
      currentSelectionOnlyLeafs: initialSelection
    });
    return of(undefined);
  }

  @Action(CheckinTemplateDetailAction.LoadReferences)
  public loadReferences(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.LoadReferences
  ): Observable<void> {
    const state = ctx.getState();
    const templateId = state.id;
    if (!templateId) {
      return throwError(() => new Error('State is not initialized!'));
    }
    if (state.referencesInProgress && !action?.force) {
      return of(undefined);
    }
    if (state.referencesLoaded && !action?.force) {
      return of(undefined);
    }

    ctx.patchState({
      referencesInProgress: true
    });

    const user = this.app.data.getUser();
    const orgId = user?.organization?.id;
    const start: Date = new Date();

    const assignedClasses$ = state.classesLoaded
      ? of(state.initialSelection)
      : this.checkinTemplateRequestService.getTargetClasses(templateId);

    return forkJoin({
      compoClasses: from(this.app.classService.getClassTreeForOrg(orgId)),
      refs: this.checkinTemplateRequestService.getReferences(templateId),
      assignedClasses: assignedClasses$
    }).pipe(
      delay(calcDefaultTimeoutOf(start)),
      map(({ compoClasses, refs, assignedClasses }) => {
        const currentState = ctx.getState();
        if (currentState.id !== templateId) {
          return;
        }

        const classNames: Record<string, string> = { ...currentState.classNames };
        for (const cl of compoClasses || []) {
          if (cl.id && cl.name) {
            classNames[cl.id] = cl.name;
          }
        }

        const referenceList = refs || [];

        const referenceClassIds = new Set<string>();
        for (const ref of referenceList) {
          for (const cid of ref.classIds || []) {
            referenceClassIds.add(cid);
          }
        }

        const updatedTemplate: CheckinTemplateDto | null = currentState.checkinTemplate
          ? {
              ...currentState.checkinTemplate,
              referenceCount: referenceList.length,
              targetClassCount: currentState.classesLoaded
                ? currentState.checkinTemplate.targetClassCount
                : (assignedClasses || []).length
            }
          : null;

        const classTree = currentState.classesLoaded
          ? (currentState.classTree ? currentState.classTree.map(node => cloneAndMarkCheckinTemplateNode(node, referenceClassIds)) : null)
          : buildCheckinTemplateClassTree(compoClasses, referenceClassIds);
        const allLeafClassIds = currentState.classesLoaded
          ? currentState.allLeafClassIds
          : getCheckinTemplateAllLeafClassIds(compoClasses);
        const allClassIds = currentState.classesLoaded
          ? currentState.allClassIds
          : (compoClasses || []).map(c => c.id).filter(Boolean);
        const initialSelection = currentState.classesLoaded
          ? currentState.initialSelection
          : (assignedClasses || []);

        ctx.patchState({
          references: referenceList,
          initialReferences: referenceList,
          referencesLoaded: true,
          classNames,
          checkinTemplate: updatedTemplate,
          disabledClassIds: Array.from(referenceClassIds),
          classTree,
          ...(currentState.classesLoaded
            ? {}
            : {
                allClassIds,
                allLeafClassIds,
                initialSelection: [...initialSelection],
                currentSelection: [...initialSelection],
                currentSelectionOnlyLeafs: [...initialSelection],
                classesLoaded: true
              })
        });
      }),
      finalize(() => {
        if (ctx.getState().id === templateId) {
          ctx.patchState({
            referencesLoaded: true,
            referencesInProgress: false
          });
        } else {
          ctx.patchState({
            referencesInProgress: false
          });
        }
      })
    );
  }

  @Action(CheckinTemplateDetailAction.AddReference)
  public addReference(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.AddReference
  ): void {
    const state = ctx.getState();
    const newRef: CheckinTemplateReferenceDto = {
      id: '',
      name: action.name,
      checkinTemplateId: state.id ?? '',
      classIds: [...action.classIds],
      required: false,
      minQuantity: null,
      orderColumn: state.references.length
    };
    const updated = [...state.references, newRef];
    const updatedTemplate: CheckinTemplateDto | null = state.checkinTemplate
      ? { ...state.checkinTemplate, referenceCount: updated.length }
      : null;
    ctx.patchState({
      references: updated,
      checkinTemplate: updatedTemplate
    });
  }

  @Action(CheckinTemplateDetailAction.UpdateReferenceClasses)
  public updateReferenceClasses(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.UpdateReferenceClasses
  ): void {
    const state = ctx.getState();
    const updated = state.references.map((ref, idx) => {
      if (idx === action.index) {
        return {
          ...ref,
          classIds: [...action.classIds]
        };
      }
      return ref;
    });
    ctx.patchState({
      references: updated
    });
  }

  @Action(CheckinTemplateDetailAction.UpdateReference)
  public updateReference(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.UpdateReference
  ): void {
    const state = ctx.getState();
    const updated = state.references.map((ref, idx) => {
      if (idx === action.index) {
        return {
          ...ref,
          name: action.name,
          required: action.required,
          minQuantity: action.required ? action.minQuantity : null
        };
      }
      return ref;
    });
    ctx.patchState({
      references: updated
    });
  }

  @Action(CheckinTemplateDetailAction.RemoveReference)
  public removeReference(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.RemoveReference
  ): void {
    const state = ctx.getState();
    const updated = state.references.filter((_, idx) => idx !== action.index);
    const updatedTemplate: CheckinTemplateDto | null = state.checkinTemplate
      ? { ...state.checkinTemplate, referenceCount: updated.length }
      : null;
    ctx.patchState({
      references: updated,
      checkinTemplate: updatedTemplate
    });
  }

  @Action(CheckinTemplateDetailAction.ReorderReferences)
  public reorderReferences(
    ctx: StateContext<CheckinTemplateDetailStateModel>,
    action: CheckinTemplateDetailAction.ReorderReferences
  ): void {
    const state = ctx.getState();
    const updated = [...state.references];
    moveItemInArray(updated, action.previousIndex, action.currentIndex);
    ctx.patchState({
      references: updated
    });
  }

  @Action(CheckinTemplateDetailAction.SaveReferences)
  public saveReferences(
    ctx: StateContext<CheckinTemplateDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();
    const templateId = state.id;
    if (!templateId) {
      return throwError(() => new Error('State is not initialized!'));
    }

    ctx.patchState({
      referencesInProgress: true
    });

    const inputs: CheckinTemplateReferenceInputDto[] = state.references.map(ref => ({
      id: ref.id || null,
      name: ref.name,
      classIds: ref.classIds,
      required: ref.required,
      minQuantity: ref.required ? (ref.minQuantity ?? null) : null
    }));

    const startDate = new Date();

    return this.checkinTemplateRequestService.saveReferences(templateId, inputs).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      switchMap(() => this.checkinTemplateRequestService.getReferences(templateId)),
      map((freshRefs: CheckinTemplateReferenceDto[]) => {
        const currentState = ctx.getState();
        if (currentState.id !== templateId) {
          return;
        }

        const refs = freshRefs || [];
        const updatedTemplate: CheckinTemplateDto | null = currentState.checkinTemplate
          ? { ...currentState.checkinTemplate, referenceCount: refs.length }
          : null;

        ctx.patchState({
          references: refs,
          initialReferences: refs,
          checkinTemplate: updatedTemplate
        });
      }),
      finalize(() => {
        ctx.patchState({
          referencesInProgress: false
        });
      })
    );
  }

  @Action(CheckinTemplateDetailAction.ResetReferences)
  public resetReferences(
    ctx: StateContext<CheckinTemplateDetailStateModel>
  ): void {
    const state = ctx.getState();
    const resetList = [...state.initialReferences];
    const updatedTemplate: CheckinTemplateDto | null = state.checkinTemplate
      ? { ...state.checkinTemplate, referenceCount: resetList.length }
      : null;
    ctx.patchState({
      references: resetList,
      checkinTemplate: updatedTemplate
    });
  }
  //endregion
}
